import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ChevronDown, ChevronUp, Lock, Download, X, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useDevice } from '../contexts/DeviceContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import FindMyGuideModal from './FindMyGuideModal';

const FindMyInstructions = () => {
  const navigate = useNavigate();
  const { selectedDevice } = useDevice();
  const { selectedChild } = useAuth();
  
  const [showFindMyInstructions, setShowFindMyInstructions] = useState(false);
  const [showPasscodeInstructions, setShowPasscodeInstructions] = useState(false);
  const [showBackupInstructions, setShowBackupInstructions] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [showFindMyGuide, setShowFindMyGuide] = useState(false);

  // Check if device is iOS 17 or lower
  const isIOS17OrLower = selectedDevice ? parseFloat(selectedDevice.iosVersion) <= 17 : true;
  
  return (
    <div className="h-full flex flex-col relative">
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-5 max-w-xl mx-auto">
          {/* Hero Section - Primary CTA */}
          <div className="text-center space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Turn Off Find My iPhone</h2>
              <p className="text-muted-foreground">
                Required before installing the filter. You can turn it back on after.
              </p>
            </div>

            {/* Start Guide Button - Hero CTA */}
            <Button
              onClick={() => setShowFindMyGuide(true)}
              size="lg"
              className="w-full h-14 text-base font-medium gap-3 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02]"
            >
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Play className="w-4 h-4 fill-current" />
              </div>
              Start Guided Instructions
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 pt-2">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Additional steps</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Passcode Instructions - Only show for iOS 17 or lower */}
          {isIOS17OrLower && (
            <Card className="border border-border/60 shadow-sm">
              <Collapsible open={showPasscodeInstructions} onOpenChange={setShowPasscodeInstructions}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                          <Lock className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-sm font-medium">Remove Passcode</CardTitle>
                            <Badge variant="destructive" className="text-xs px-1.5 py-0">iOS 17-</Badge>
                          </div>
                          <CardDescription className="text-xs">
                            Required for iOS 17 and below
                          </CardDescription>
                        </div>
                      </div>
                      {showPasscodeInstructions ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      <Alert className="border-amber-200 bg-amber-50">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <AlertDescription className="text-amber-800 text-base">
                          This step is only required if your device is running iOS 17 or lower. iOS 18+ users can skip this step.
                        </AlertDescription>
                      </Alert>

                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-blue-600 font-semibold text-sm">1</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-base mb-2">Open Settings</h4>
                          <p className="text-gray-600 text-base">On your iPhone, tap the Settings app</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-blue-600 font-semibold text-sm">2</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-base mb-2">Go to Face ID & Passcode</h4>
                          <p className="text-gray-600 text-base">Tap "Face ID & Passcode" (or "Touch ID & Passcode" on older devices)</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-blue-600 font-semibold text-sm">3</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-base mb-2">Enter Current Passcode</h4>
                          <p className="text-gray-600 text-base">Enter your current device passcode</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-blue-600 font-semibold text-sm">4</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-base mb-2">Turn Off Passcode</h4>
                          <p className="text-gray-600 text-base">Tap "Turn Passcode Off" and confirm when prompted</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}

          {/* Backup Instructions - Recommended */}
          <Card className="border border-border/60 shadow-sm">
            <Collapsible open={showBackupInstructions} onOpenChange={setShowBackupInstructions}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Download className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-sm font-medium">Back Up Your Phone</CardTitle>
                          <Badge className="text-xs px-1.5 py-0 bg-emerald-100 text-emerald-700 border-emerald-200">Optional</Badge>
                        </div>
                        <CardDescription className="text-xs">
                          Recommended before setup
                        </CardDescription>
                      </div>
                    </div>
                    {showBackupInstructions ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <Alert className="border-green-200 bg-green-50">
                      <Download className="h-5 w-5 text-green-600" />
                      <AlertDescription className="text-green-800 text-base">
                        This shouldn't be necessary, but out of an abundance of caution we recommend backing up your phone before installing the filter.
                      </AlertDescription>
                    </Alert>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-blue-600 font-semibold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-base mb-2">Open Settings</h4>
                        <p className="text-gray-600 text-base">On your iPhone, tap the Settings app</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-blue-600 font-semibold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-base mb-2">Tap Your Name</h4>
                        <p className="text-gray-600 text-base">At the top of Settings, tap your name to open Apple ID settings</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-blue-600 font-semibold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-base mb-2">Select iCloud</h4>
                        <p className="text-gray-600 text-base">Tap "iCloud" from the menu</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-blue-600 font-semibold text-sm">4</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-base mb-2">Tap iCloud Backup</h4>
                        <p className="text-gray-600 text-base">Scroll down and select "iCloud Backup"</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-blue-600 font-semibold text-sm">5</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 text-base mb-2">Back Up Now</h4>
                        <p className="text-gray-600 text-base">Tap "Back Up Now" and wait for the backup to complete</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>


        </div>
      </ScrollArea>

      {/* Fixed Action Button at Bottom */}
      <div className="border-t bg-background px-6 py-4 flex justify-center">
        <Button 
          size="lg" 
          className="bg-shomri-blue hover:bg-shomri-blue/90 h-11 px-8 text-sm font-medium"
          onClick={() => setShowConfirmation(true)}
        >
          Install Filter
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Custom Slide-up Confirmation */}
      {showConfirmation && (
        <>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowConfirmation(false)}
          />
          
          {/* Slide-up Panel */}
          <div className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-lg z-50 transform transition-transform duration-300 ${showConfirmation ? 'translate-y-0' : 'translate-y-full'}`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Ready to Install Filter?</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowConfirmation(false)}
                  className="p-1"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Description */}
              <p className="text-sm text-gray-600 mb-6">
                Before proceeding, please confirm that you have completed all the required preparation steps.
              </p>
              
              {/* Checklist */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">Turned off Find My iPhone</span>
                </div>
                {isIOS17OrLower && (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-700">Removed device passcode (iOS 17 or lower only)</span>
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">Backed up your phone (recommended)</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-col space-y-2">
                <Button 
                  onClick={() => navigate('/app-selection')} 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={showDownload}
                >
                  Yes, Continue
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowConfirmation(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Find My iPhone Guide Modal */}
      <FindMyGuideModal 
        open={showFindMyGuide} 
        onOpenChange={setShowFindMyGuide} 
      />
    </div>
  );
};
export default FindMyInstructions;